import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, UploadCloud, FileText, CheckCircle2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CONTACT, SERVICE_OPTIONS } from "../lib/site";
import { MaskLines, Reveal } from "../components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full h-14 px-4 rounded-lg bg-forest border border-white/10 text-cream placeholder:text-cream/40 font-body focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors duration-200";

export default function Careers() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", trade: "", experience: "" });
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      if (f.size > 8 * 1024 * 1024) {
        toast.error("CV file is too large (max 8MB).");
        return;
      }
      setFile(f);
      setFileName(f.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("phone", form.phone);
      data.append("email", form.email);
      data.append("trade", form.trade);
      data.append("experience", form.experience);
      if (file) data.append("cv", file);
      await axios.post(`${API}/careers`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application sent! Thanks for applying — we'll be in touch.");
      setForm({ name: "", phone: "", email: "", trade: "", experience: "" });
      setFile(null);
      setFileName("");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="careers-page">
      <section className="relative min-h-[60vh] flex items-end bg-forest-deep pt-36 pb-16 overflow-hidden noise-overlay">
        <div className="absolute -top-20 -right-20 h-[420px] w-[420px] rounded-full bg-brand-orange/10 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 sm:px-8">
          <button onClick={() => navigate("/")} className="flex w-fit items-center gap-2 text-cream/60 hover:text-cream mb-8 font-display uppercase tracking-[0.14em] text-xs transition-colors" data-testid="careers-back">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </button>
          <span className="block font-display uppercase tracking-[0.25em] text-xs text-brand-orange">Careers</span>
          <h1 className="mt-4 font-display uppercase tracking-tighter leading-[0.9] text-cream text-[16vw] sm:text-[10rem]">
            <MaskLines lines={["Join Our", "Team"]} lineClassName="text-cream" />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }} className="mt-6 max-w-xl text-cream/75 text-lg leading-relaxed">
            We are always looking for skilled, reliable, and dedicated tradespeople to
            join Bezalel Property Services. Apply below!
          </motion.p>
        </div>
      </section>

      <section className="relative bg-forest py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-forest-surface p-6 sm:p-10" data-testid="careers-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input className={fieldClass} placeholder="Full Name *" value={form.name} onChange={set("name")} data-testid="careers-name" />
                <input className={fieldClass} placeholder="Phone Number" type="tel" value={form.phone} onChange={set("phone")} data-testid="careers-phone" />
                <input className={fieldClass} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="careers-email" />
                <select className={`${fieldClass} appearance-none`} value={form.trade} onChange={set("trade")} data-testid="careers-trade">
                  <option value="">Trade / Specialty</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-forest text-cream">{s}</option>
                  ))}
                  <option value="Other" className="bg-forest text-cream">Other</option>
                </select>
                <input className={`${fieldClass} sm:col-span-2`} placeholder="Experience (years)" type="number" min="0" value={form.experience} onChange={set("experience")} data-testid="careers-experience" />
              </div>

              <label className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-forest px-4 py-8 cursor-pointer hover:border-brand-orange/60 transition-colors duration-300" data-testid="careers-cv-label">
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFile} data-testid="careers-cv-input" />
                {fileName ? (
                  <span className="flex items-center gap-2 text-cream"><FileText className="h-5 w-5 text-brand-orange" /> {fileName}</span>
                ) : (
                  <>
                    <UploadCloud className="h-8 w-8 text-brand-orange" />
                    <span className="font-display uppercase tracking-wider text-cream">Attach your CV</span>
                    <span className="text-cream/50 text-sm">PDF, DOC or DOCX</span>
                  </>
                )}
              </label>

              <div className="mt-4 flex items-start gap-2 text-cream/50 text-xs">
                <CheckCircle2 className="h-4 w-4 text-brand-orange shrink-0 mt-0.5" />
                <span>Submitting opens a Gmail compose window pre-filled with your application — please attach the selected CV file before sending.</span>
              </div>

              <button type="submit" className="mt-8 w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-brand-orange hover:bg-brand-orangeHover text-white font-display uppercase tracking-wider text-base transition-[background-color,transform] duration-300 hover:-translate-y-1" data-testid="careers-submit">
                <Send className="h-5 w-5" /> Submit Application
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
